import React from 'react';
import { 
  Mail, 
  Phone, 
  Globe, 
  Instagram, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Youtube, 
  Link 
} from 'lucide-react';
import { Category } from '../../types';
import { buildContactOpenUrl, resolveContactChannel } from '../../utils/contactOutbound';
import WhatsAppIcon from './WhatsAppIcon';

interface ContactIconWithCategoryProps {
  category?: Category;
  value: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

const ContactIconWithCategory: React.FC<ContactIconWithCategoryProps> = ({ 
  category, 
  value, 
  className = '', 
  size = 'md',
  showTooltip = true 
}) => {
  const TikTokIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  // Map category labels to contact types for backward compatibility
  const getContactType = (label: string) => {
    const normalizedLabel = label.toLowerCase();
    if (normalizedLabel.includes('whatsapp')) return 'whatsapp';
    if (normalizedLabel.includes('email') || normalizedLabel.includes('mail')) return 'email';
    if (normalizedLabel.includes('phone') || normalizedLabel.includes('téléphone')) return 'phone';
    if (normalizedLabel.includes('website') || normalizedLabel.includes('site')) return 'website';
    if (normalizedLabel.includes('instagram')) return 'instagram';
    if (normalizedLabel.includes('tiktok')) return 'tiktok';
    if (normalizedLabel.includes('facebook')) return 'facebook';
    if (normalizedLabel.includes('twitter')) return 'twitter';
    if (normalizedLabel.includes('linkedin')) return 'linkedin';
    if (normalizedLabel.includes('youtube')) return 'youtube';
    return 'other';
  };

  const getIcon = () => {
    if (!category) return <Link className="w-4 h-4" />;
    const effectiveLabel = category.label_fr ?? category.label_en ?? category.label ?? '';
    const type = getContactType(effectiveLabel);
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'phone':
        return <Phone className="w-4 h-4" />;
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'website':
        return <Globe className="w-4 h-4" />;
      case 'instagram':
        return <Instagram className="w-4 h-4" />;
      case 'tiktok':
        return <TikTokIcon />;
      case 'facebook':
        return <Facebook className="w-4 h-4" />;
      case 'twitter':
        return <Twitter className="w-4 h-4" />;
      case 'linkedin':
        return <Linkedin className="w-4 h-4" />;
      case 'youtube':
        return <Youtube className="w-4 h-4" />;
      case 'other':
      default:
        return <Link className="w-4 h-4" />;
    }
  };

  const getHref = () => {
    if (!category) return value;
    const channel = resolveContactChannel(category);
    const built = buildContactOpenUrl(channel, value);
    if (built) return built;
    const effectiveLabel = category.label_fr ?? category.label_en ?? category.label ?? '';
    const type = getContactType(effectiveLabel);
    if (type === 'phone') return `tel:${value}`;
    return value;
  };

  const iconElement = (
    <span className={`inline-flex shrink-0 items-center ${className}`}>
      {getIcon()}
    </span>
  );

  if (showTooltip && category) {
    const effectiveLabel = category.label_fr ?? category.label_en ?? category.label ?? '';
    const type = getContactType(effectiveLabel);
    if (type === 'email' || type === 'phone' || type === 'website') {
      return (
        <a 
          href={getHref()} 
          target={type === 'website' ? '_blank' : undefined}
          rel={type === 'website' ? 'noopener noreferrer' : undefined}
          className="inline-flex shrink-0 items-center hover:opacity-80 transition-opacity"
        >
          {iconElement}
        </a>
      );
    }
  }

  return iconElement;
};

export default ContactIconWithCategory;



